import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { compareMonth, getMetricLabel } from '@/modules/finances/lib/budget-statement-utils';
import type { BudgetStatementsTableColumn } from '@/modules/finances/lib/constants';
import type { BudgetStatement, MetricWithoutBudget } from '@/modules/finances/types';
import { useMediaQuery } from '@/modules/shared/hooks/use-media-query';
import type SimpleBar from 'simplebar-react';
interface UseBudgetStatementTableProps {
  budgetStatements: BudgetStatement[];
  budgetMetric: MetricWithoutBudget;
  asSectionContent?: boolean;
}
export enum SortEnum {
  Neutral = 'neutral',
  Asc = 'asc',
  Desc = 'desc',
  Disabled = 'disabled',
}
export function useBudgetStatementTable(t0) {
  const $ = _c(41);
  const {
    budgetStatements,
    budgetMetric,
    asSectionContent: t1
  } = t0;
  const asSectionContent = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      from: "lg",
      to: "xl"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const isDesktopLg = useMediaQuery(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      from: "lg"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const isDesktop = useMediaQuery(t3);
  const simpleBarRef = useRef(null);
  const cardContentRef = useRef(null);
  const itemsWrapperRef = useRef(null);
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      header: "Builders",
      accessorKey: "owner.name",
      hasSort: true,
      sortReverse: false,
      isNumeric: false
    };
    t5 = {
      header: "Reporting Month",
      accessorKey: "month",
      hasSort: true,
      sortReverse: false,
      isNumeric: false
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  if ($[4] !== budgetMetric || $[5] !== isDesktopLg) {
    t6 = getMetricLabel(budgetMetric, isDesktopLg);
    $[4] = budgetMetric;
    $[5] = isDesktopLg;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = {
      header: t6,
      accessorKey: "",
      hasSort: false,
      sortReverse: false,
      isNumeric: false
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      header: "Status",
      accessorKey: "status",
      hasSort: false,
      sortReverse: false,
      isNumeric: false
    };
    t9 = {
      header: "Last Modified",
      accessorKey: "lastModifiedAtUtcIso",
      hasSort: true,
      sortReverse: true,
      isNumeric: false
    };
    $[9] = t8;
    $[10] = t9;
  } else {
    t8 = $[9];
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t7) {
    t10 = [t4, t5, t7, t8, t9];
    $[11] = t7;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const BUDGET_STATEMENTS_TABLE_COLUMNS = t10;
  let t11;
  let t12;
  if ($[13] !== asSectionContent || $[14] !== budgetStatements.length || $[15] !== isDesktop) {
    t11 = () => {
      if (!asSectionContent) {
        return;
      }
      const simpleBarEl = simpleBarRef.current?.el;
      const cardContentEl = cardContentRef.current;
      const itemsWrapperEl = itemsWrapperRef.current;
      if (!simpleBarEl || !cardContentEl || !itemsWrapperEl) {
        return;
      }
      const teamsCount = budgetStatements.length;
      let config = null;
      if (isDesktop) {
        config = {
          maxHeight: "630px",
          maxItems: 9
        };
      }
      if (!config) {
        simpleBarEl.style.maxHeight = "";
        cardContentEl.style.paddingRight = "";
        itemsWrapperEl.style.paddingRight = "";
        itemsWrapperEl.style.paddingLeft = "";
        itemsWrapperEl.style.paddingBottom = "";
        return;
      }
      if (teamsCount > config.maxItems) {
        simpleBarEl.style.maxHeight = config.maxHeight;
        cardContentEl.style.paddingRight = "4px";
        itemsWrapperEl.style.paddingRight = "12px";
      }
    };
    t12 = [isDesktop, budgetStatements.length, asSectionContent];
    $[13] = asSectionContent;
    $[14] = budgetStatements.length;
    $[15] = isDesktop;
    $[16] = t11;
    $[17] = t12;
  } else {
    t11 = $[16];
    t12 = $[17];
  }
  useEffect(t11, t12);
  let t13;
  if ($[18] !== BUDGET_STATEMENTS_TABLE_COLUMNS) {
    t13 = BUDGET_STATEMENTS_TABLE_COLUMNS.map(_temp);
    $[18] = BUDGET_STATEMENTS_TABLE_COLUMNS;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const [headersSort, setHeadersSort] = useState(t13);
  const [sortColumn, setSortColumn] = useState(-1);
  let t14;
  if ($[20] !== BUDGET_STATEMENTS_TABLE_COLUMNS || $[21] !== isDesktop) {
    t14 = BUDGET_STATEMENTS_TABLE_COLUMNS.map(column_0 => ({
      accessorKey: column_0.accessorKey,
      hasSort: column_0.hasSort,
      sortReverse: column_0.sortReverse,
      isNumeric: column_0.isNumeric,
      header: !isDesktop && column_0.shortHeader ? column_0.shortHeader : column_0.header
    }));
    $[20] = BUDGET_STATEMENTS_TABLE_COLUMNS;
    $[21] = isDesktop;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const proccesedBudgetStatementsTableColumns = t14;
  let t15;
  if ($[23] !== BUDGET_STATEMENTS_TABLE_COLUMNS || $[24] !== headersSort) {
    t15 = index => {
      const sortNeutralState = BUDGET_STATEMENTS_TABLE_COLUMNS.map(_temp2) as SortEnum[];
      if (headersSort[index] === SortEnum.Neutral) {
        if (BUDGET_STATEMENTS_TABLE_COLUMNS[index].sortReverse) {
          sortNeutralState[index] = SortEnum.Desc;
        } else {
          sortNeutralState[index] = SortEnum.Asc;
        }
      } else {
        sortNeutralState[index] = headersSort[index] === SortEnum.Asc ? SortEnum.Desc : SortEnum.Asc;
      }
      setHeadersSort(sortNeutralState);
      setSortColumn(index);
    };
    $[23] = BUDGET_STATEMENTS_TABLE_COLUMNS;
    $[24] = headersSort;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const handleSortClick = t15;
  const getNestedValue = _temp3;
  let t16;
  if ($[26] !== BUDGET_STATEMENTS_TABLE_COLUMNS || $[27] !== headersSort || $[28] !== sortColumn) {
    t16 = statements => {
      if (sortColumn === -1) {
        return statements;
      }
      const column_2 = BUDGET_STATEMENTS_TABLE_COLUMNS[sortColumn];
      const sortDirection = headersSort[sortColumn];
      const effectiveSortDirection = column_2.sortReverse && sortDirection !== SortEnum.Neutral && sortDirection !== SortEnum.Disabled ? sortDirection === SortEnum.Asc ? SortEnum.Desc : SortEnum.Asc : sortDirection;
      if (sortDirection === SortEnum.Neutral || sortDirection === SortEnum.Disabled) {
        return statements;
      }
      return [...statements].sort((a, b) => {
        const getSortableValue = statement => {
          if (column_2.accessorKey === "lastModifiedAtUtcIso") {
            if (!statement.lastModifiedAtUtcIso) {
              return -1;
            }
            const timestamp = new Date(statement.lastModifiedAtUtcIso).getTime();
            return Number.isNaN(timestamp) ? -1 : timestamp;
          }
          const value = getNestedValue(statement, column_2.accessorKey);
          if (value === null) {
            return "";
          }
          if (typeof value === "string") {
            return value.toLowerCase();
          }
          return value;
        };
        if (column_2.accessorKey === "month") {
          const result_0 = compareMonth(a.month, b.month);
          return effectiveSortDirection === SortEnum.Asc ? result_0 : -result_0;
        }
        const aValue = getSortableValue(a);
        const bValue = getSortableValue(b);
        if (aValue < bValue) {
          return effectiveSortDirection === SortEnum.Asc ? -1 : 1;
        }
        if (aValue > bValue) {
          return effectiveSortDirection === SortEnum.Asc ? 1 : -1;
        }
        return 0;
      });
    };
    $[26] = BUDGET_STATEMENTS_TABLE_COLUMNS;
    $[27] = headersSort;
    $[28] = sortColumn;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const sortBudgetStatements = t16;
  let t17;
  if ($[30] !== budgetStatements || $[31] !== sortBudgetStatements) {
    t17 = sortBudgetStatements(budgetStatements);
    $[30] = budgetStatements;
    $[31] = sortBudgetStatements;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const sortedBudgetStatements = t17;
  let t18;
  if ($[33] !== handleSortClick || $[34] !== proccesedBudgetStatementsTableColumns) {
    t18 = index_0 => {
      if (proccesedBudgetStatementsTableColumns[index_0].hasSort) {
        handleSortClick(index_0);
      }
    };
    $[33] = handleSortClick;
    $[34] = proccesedBudgetStatementsTableColumns;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const handleSortClickHeader = t18;
  let t19;
  if ($[36] !== handleSortClickHeader || $[37] !== headersSort || $[38] !== proccesedBudgetStatementsTableColumns || $[39] !== sortedBudgetStatements) {
    t19 = {
      proccesedBudgetStatementsTableColumns,
      headersSort,
      sortedBudgetStatements,
      simpleBarRef,
      cardContentRef,
      itemsWrapperRef,
      handleSortClickHeader
    };
    $[36] = handleSortClickHeader;
    $[37] = headersSort;
    $[38] = proccesedBudgetStatementsTableColumns;
    $[39] = sortedBudgetStatements;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  return t19;
}
function _temp3(obj, path) {
  const keys = path.split(".");
  let result = obj;
  for (const key of keys) {
    if (result === null || result === undefined) {
      return null;
    }
    result = (result as Record<string, unknown>)[key];
  }
  if (typeof result === "string" || typeof result === "number") {
    return result;
  }
  return null;
}
function _temp2(column_1) {
  return column_1.hasSort ? SortEnum.Neutral : SortEnum.Disabled;
}
function _temp(column) {
  return column.hasSort ? SortEnum.Neutral : SortEnum.Disabled;
}